import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { User, Camera, Shield, Bell, Palette, Save, Check, Sparkles, Heart, ArrowRight, MessageSquareHeart, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const COACHING_STYLES = [
  { value: 'nurturing', label: 'Nurturing', desc: 'Gentle guidance with deep warmth and compassion' },
  { value: 'balanced', label: 'Balanced', desc: 'Warm encouragement with honest directness' },
  { value: 'direct', label: 'Direct', desc: 'Bold truth-telling with loving confrontation' },
];

export default function ProfilePage() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [identity, setIdentity] = useState('');
  const [savingIdentity, setSavingIdentity] = useState(false);
  const [enneagram, setEnneagram] = useState(null);
  const [prefs, setPrefs] = useState({ coaching_style: 'balanced', daily_reminders: true, theme: 'light' });
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [deleteState, setDeleteState] = useState({ open: false, password: '', confirmText: '', submitting: false });
  const fileInputRef = useRef(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setIdentity(user.current_identity || '');
    }
    loadPreferences();
    loadEnneagram();
  }, [user]);

  const loadPreferences = async () => {
    try {
      const res = await api.get('/auth/preferences');
      setPrefs(res.data);
    } catch { /* use defaults */ }
  };

  const loadEnneagram = async () => {
    try {
      const res = await api.get('/onboarding/result');
      setEnneagram(res.data);
    } catch {
      setEnneagram(null);
    }
  };

  const getAvatarUrl = (picture) => {
    if (!picture) return null;
    if (picture.startsWith('http')) return picture;
    return `${BACKEND_URL}/api/files/${picture}?auth=${document.cookie.split('session_token=')[1]?.split(';')[0] || ''}`;
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarPreview(URL.createObjectURL(file));
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post('/auth/avatar', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Avatar updated');
      checkAuth();
    } catch (err) {
      toast.error('Could not upload avatar');
      setAvatarPreview(null);
    }
  };

  const saveName = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await api.put('/auth/profile', { name: name.trim() });
      toast.success('Name updated');
      checkAuth();
    } catch (err) {
      toast.error('Could not update name');
    } finally { setSaving(false); }
  };

  const saveIdentity = async () => {
    setSavingIdentity(true);
    try {
      await api.put('/auth/profile', { current_identity: identity.trim() });
      toast.success('Identity updated');
      checkAuth();
    } catch {
      toast.error('Could not save identity');
    } finally { setSavingIdentity(false); }
  };

  const savePreferences = async (updates) => {
    try {
      const res = await api.put('/auth/preferences', updates);
      setPrefs(res.data);
      toast.success('Preferences saved');
    } catch (err) {
      toast.error('Could not save preferences');
    }
  };

  const changePassword = async () => {
    if (!currentPassword || !newPassword) return;
    try {
      await api.post('/auth/change-password', { current_password: currentPassword, new_password: newPassword });
      toast.success('Password changed');
      setCurrentPassword('');
      setNewPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not change password');
    }
  };

  const submitSelfDelete = async () => {
    if (deleteState.confirmText !== 'DELETE MY ACCOUNT') {
      toast.error('Type exactly: DELETE MY ACCOUNT');
      return;
    }
    setDeleteState(s => ({ ...s, submitting: true }));
    try {
      await api.post('/auth/delete-account', {
        password: deleteState.password,
        confirm: 'DELETE MY ACCOUNT',
      });
      toast.success('Your account is deleted. You have 30 days to change your mind — email support to restore.');
      // Force logout by reloading; backend cleared the cookie too.
      setTimeout(() => { window.location.href = '/login'; }, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete account');
      setDeleteState(s => ({ ...s, submitting: false }));
    }
  };

  const initials = user?.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';
  const hasPassword = true; // We show the section; backend validates

  return (
    <div className="max-w-2xl mx-auto space-y-10" data-testid="profile-page">
      {/* Header */}
      <div className="animate-fade-up">
        <p className="text-gold font-sans text-xs tracking-[0.3em] uppercase mb-2">Your Identity</p>
        <h1 className="font-serif text-4xl sm:text-5xl font-light text-dark tracking-tight mystical-heading">Profile & Settings</h1>
        <div className="mt-5">
          <button
            onClick={() => window.dispatchEvent(new Event('open-feedback'))}
            data-testid="profile-feedback-button"
            className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-bone/70 hover:bg-bone border border-gold/25 font-sans text-xs tracking-[0.2em] uppercase text-dark"
            style={{ transition: 'background-color 0.2s ease' }}
          >
            <MessageSquareHeart size={14} className="text-gold" /> Send Feedback
          </button>
        </div>
      </div>

      {/* Avatar & Name */}
      <div className="mystical-card p-8 animate-fade-up delay-100" data-testid="profile-avatar-section">
        <div className="flex items-center gap-2 mb-6">
          <User size={16} className="text-gold" />
          <span className="text-gold font-sans text-xs tracking-[0.25em] uppercase">Personal Info</span>
        </div>
        <div className="flex flex-col sm:flex-row items-center gap-6">
          <div className="relative group">
            <Avatar className="h-24 w-24 ring-2 ring-gold/20">
              <AvatarImage src={avatarPreview || getAvatarUrl(user?.picture)} />
              <AvatarFallback className="bg-gold/15 text-dark font-serif text-2xl">{initials}</AvatarFallback>
            </Avatar>
            <button
              onClick={() => fileInputRef.current?.click()}
              data-testid="upload-avatar-button"
              className="absolute inset-0 flex items-center justify-center rounded-full bg-dark/40 opacity-0 group-hover:opacity-100"
              style={{ transition: 'opacity 0.2s ease' }}
            >
              <Camera size={20} className="text-bone" />
            </button>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarUpload} className="hidden" />
          </div>
          <div className="flex-1 w-full space-y-3">
            <div>
              <Label className="text-text-muted text-xs tracking-wider uppercase font-sans">Display Name</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="profile-name-input"
                  className="h-11 bg-sand/50 border-black/8 focus:border-gold focus:ring-gold/30 rounded-xl font-sans"
                />
                <Button
                  onClick={saveName}
                  disabled={saving || name === user?.name}
                  data-testid="save-name-button"
                  className="h-11 bg-dark hover:bg-dark/90 text-bone rounded-xl font-sans text-sm btn-hover shrink-0"
                >
                  <Save size={14} className="mr-1" /> Save
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-text-muted text-xs tracking-wider uppercase font-sans">Email</Label>
              <p className="font-sans text-sm text-dark mt-1">{user?.email}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Current Identity */}
      <div className="mystical-card p-8 animate-fade-up delay-150" data-testid="identity-section">
        <div className="flex items-center gap-2 mb-5">
          <Heart size={16} className="text-gold" />
          <span className="text-gold font-sans text-xs tracking-[0.25em] uppercase">Who You Are Becoming</span>
        </div>
        <p className="text-text-muted font-sans text-sm mb-4 leading-relaxed">
          Describe the version of you who already has what you want. Write in present tense — as if it's already true. Your coach will reference this in your sessions.
        </p>
        <Textarea
          value={identity}
          onChange={(e) => setIdentity(e.target.value)}
          data-testid="current-identity-input"
          placeholder="I'm a woman who trusts herself completely. I move through the world with grounded presence. My desires are already on their way..."
          rows={5}
          className="bg-sand/50 border-black/8 focus:border-gold focus:ring-gold/30 rounded-xl font-sans text-base leading-relaxed resize-none"
        />
        <div className="flex justify-end mt-4">
          <Button
            onClick={saveIdentity}
            disabled={savingIdentity || identity === (user?.current_identity || '')}
            data-testid="save-identity-button"
            className="h-11 bg-dark hover:bg-dark/90 text-bone rounded-xl font-sans text-sm btn-hover"
          >
            <Save size={14} className="mr-2" /> {savingIdentity ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      {/* Enneagram */}
      <div className="mystical-card p-8 animate-fade-up delay-200" data-testid="enneagram-section">
        <div className="flex items-center gap-2 mb-5">
          <Sparkles size={16} className="text-gold" />
          <span className="text-gold font-sans text-xs tracking-[0.25em] uppercase">Your Enneagram Pattern</span>
        </div>
        {enneagram ? (
          <div>
            <p className="text-text-muted font-sans text-xs tracking-wider uppercase mb-2">Dominant Type</p>
            <h3 className="font-serif text-2xl sm:text-3xl text-dark leading-tight mb-1" data-testid="enneagram-dominant-name">
              Type {enneagram.dominant.type} — {enneagram.dominant.name}
            </h3>
            <p className="font-sans text-sm text-text-muted italic mb-5">{enneagram.dominant.subtitle}</p>
            <p className="font-sans text-sm text-dark leading-relaxed mb-6">{enneagram.dominant.description}</p>

            <p className="text-gold font-sans text-xs tracking-[0.25em] uppercase mb-3">Top 3 Patterns</p>
            <div className="space-y-2 mb-6">
              {enneagram.top_3.map((t, i) => (
                <div
                  key={t.type}
                  className={`flex items-center justify-between p-3 rounded-xl ${
                    i === 0 ? 'bg-gold/10 border border-gold/30' : 'bg-bone/60 border border-black/8'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-serif text-sm ${
                      i === 0 ? 'bg-gold text-dark' : 'bg-black/8 text-dark'
                    }`}>{t.type}</div>
                    <div>
                      <p className="font-serif text-base text-dark leading-tight">{t.name}</p>
                      <p className="font-sans text-xs text-text-muted">{t.subtitle}</p>
                    </div>
                  </div>
                  <p className="font-serif text-lg text-dark">{t.score}%</p>
                </div>
              ))}
            </div>

            <details className="mt-4">
              <summary className="cursor-pointer text-gold font-sans text-xs tracking-[0.25em] uppercase py-2 hover:text-gold/80" data-testid="enneagram-details-toggle">
                View your gifts & shadows
              </summary>
              <div className="pt-4 space-y-5">
                <div>
                  <p className="text-gold font-sans text-xs tracking-[0.2em] uppercase mb-2">Natural Gifts</p>
                  <ul className="space-y-2">
                    {enneagram.dominant.positive_aspects.map((item, i) => (
                      <li key={i} className="flex gap-2 font-sans text-sm text-dark">
                        <span className="text-gold mt-1">✦</span><span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-gold font-sans text-xs tracking-[0.2em] uppercase mb-2">Likely Shadows</p>
                  <ul className="space-y-2">
                    {enneagram.dominant.pitfalls.map((item, i) => (
                      <li key={i} className="flex gap-2 font-sans text-sm text-text-muted">
                        <span className="text-gold/70 mt-1">◌</span><span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="p-4 rounded-xl bg-dark text-bone">
                  <p className="text-gold font-sans text-xs tracking-[0.2em] uppercase mb-2">Manifestation Path</p>
                  <p className="font-serif text-base italic leading-relaxed">"{enneagram.dominant.manifestation_insight}"</p>
                </div>
              </div>
            </details>
            <div className="pt-5 mt-5 border-t border-black/8">
              <Button
                onClick={() => navigate('/onboarding')}
                variant="ghost"
                data-testid="retake-enneagram-button"
                className="h-10 text-text-muted hover:text-dark font-sans text-xs tracking-wider uppercase"
              >
                Retake the Inquiry
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <p className="text-text-muted font-sans text-sm mb-5 leading-relaxed">
              You haven't taken the 36-question Enneagram inquiry yet. Taking it helps your coach meet you with clearer attunement — knowing your natural gifts and likely shadows.
            </p>
            <Button
              onClick={() => navigate('/onboarding')}
              data-testid="take-enneagram-button"
              className="h-11 bg-dark hover:bg-dark/90 text-bone rounded-xl font-sans text-sm btn-hover"
            >
              Take the Inquiry <ArrowRight size={14} className="ml-2" />
            </Button>
          </div>
        )}
      </div>

      {/* Coaching Style */}
      <div className="mystical-card p-8 animate-fade-up delay-200" data-testid="coaching-style-section">
        <div className="flex items-center gap-2 mb-6">
          <Palette size={16} className="text-gold" />
          <span className="text-gold font-sans text-xs tracking-[0.25em] uppercase">Coaching Style</span>
        </div>
        <p className="text-text-muted font-sans text-sm mb-5 leading-relaxed">
          Choose how your AI coach communicates. This shapes the balance of warmth and directness in your sessions.
        </p>
        <div className="space-y-3">
          {COACHING_STYLES.map((style) => (
            <button
              key={style.value}
              onClick={() => savePreferences({ coaching_style: style.value })}
              data-testid={`coaching-style-${style.value}`}
              className={`w-full text-left p-4 rounded-xl border ${
                prefs.coaching_style === style.value
                  ? 'border-gold/40 bg-gold/8 gold-border-glow'
                  : 'border-black/8 bg-bone/40 hover:border-gold/20'
              }`}
              style={{ transition: 'border-color 0.3s ease, background-color 0.3s ease, box-shadow 0.3s ease' }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-serif text-lg text-dark">{style.label}</p>
                  <p className="text-text-muted font-sans text-xs mt-0.5">{style.desc}</p>
                </div>
                {prefs.coaching_style === style.value && <Check size={18} className="text-gold" />}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Notifications */}
      <div className="mystical-card p-8 animate-fade-up delay-300" data-testid="notifications-section">
        <div className="flex items-center gap-2 mb-6">
          <Bell size={16} className="text-gold" />
          <span className="text-gold font-sans text-xs tracking-[0.25em] uppercase">Notifications</span>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="font-serif text-lg text-dark">Daily Reflection Reminders</p>
            <p className="text-text-muted font-sans text-xs mt-0.5">Receive a daily prompt to inspire your journaling practice</p>
          </div>
          <button
            onClick={() => savePreferences({ daily_reminders: !prefs.daily_reminders })}
            data-testid="toggle-daily-reminders"
            className={`w-12 h-7 rounded-full relative ${
              prefs.daily_reminders ? 'bg-gold' : 'bg-black/15'
            }`}
            style={{ transition: 'background-color 0.3s ease' }}
          >
            <div
              className={`absolute top-0.5 w-6 h-6 rounded-full bg-bone shadow-sm ${
                prefs.daily_reminders ? 'left-[calc(100%-1.625rem)]' : 'left-0.5'
              }`}
              style={{ transition: 'left 0.3s ease' }}
            />
          </button>
        </div>
      </div>

      {/* Change Password */}
      <div className="mystical-card p-8 animate-fade-up delay-400" data-testid="password-section">
        <div className="flex items-center gap-2 mb-6">
          <Shield size={16} className="text-gold" />
          <span className="text-gold font-sans text-xs tracking-[0.25em] uppercase">Security</span>
        </div>
        <div className="space-y-4">
          <div>
            <Label className="text-text-muted text-xs tracking-wider uppercase font-sans">Current Password</Label>
            <Input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              data-testid="current-password-input"
              placeholder="Enter current password"
              className="h-11 mt-1 bg-sand/50 border-black/8 focus:border-gold focus:ring-gold/30 rounded-xl font-sans"
            />
          </div>
          <div>
            <Label className="text-text-muted text-xs tracking-wider uppercase font-sans">New Password</Label>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              data-testid="new-password-input"
              placeholder="Enter new password"
              className="h-11 mt-1 bg-sand/50 border-black/8 focus:border-gold focus:ring-gold/30 rounded-xl font-sans"
            />
          </div>
          <Button
            onClick={changePassword}
            disabled={!currentPassword || !newPassword}
            data-testid="change-password-button"
            className="bg-dark hover:bg-dark/90 text-bone rounded-xl font-sans text-sm tracking-wider btn-hover"
          >
            Update Password
          </Button>
        </div>
      </div>

      {/* Danger Zone — Self Delete */}
      {!user?.is_admin && (
        <div className="mystical-card p-8 animate-fade-up delay-500 border-red-200/50" data-testid="danger-zone-section">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={16} className="text-red-600" />
            <span className="text-red-700 font-sans text-xs tracking-[0.25em] uppercase">Danger Zone</span>
          </div>
          <p className="font-serif text-xl text-dark mb-2">Delete my account</p>
          <p className="text-text-muted font-sans text-sm leading-relaxed mb-5 max-w-2xl">
            Your account will be disabled right away and permanently deleted after 30 days. During that window,
            email <a href="mailto:client_care@liveinvibrancy.com" className="text-gold hover:underline">client_care@liveinvibrancy.com</a> to restore it. After 30 days, your conversations, journal entries, signs, and data will be gone for good.
          </p>
          <Button
            onClick={() => setDeleteState({ open: true, password: '', confirmText: '', submitting: false })}
            data-testid="open-delete-account-button"
            variant="outline"
            className="border-red-300 bg-red-50/40 hover:bg-red-50 text-red-700 rounded-xl font-sans text-xs tracking-[0.25em] uppercase h-11 px-5"
          >
            <Trash2 size={14} className="mr-2" /> Delete my account
          </Button>
        </div>
      )}

      {/* Self-delete confirmation modal */}
      {deleteState.open && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6"
          data-testid="self-delete-modal"
          style={{ background: 'rgba(15,16,16,0.55)', backdropFilter: 'blur(8px)' }}
          onClick={() => !deleteState.submitting && setDeleteState({ open: false, password: '', confirmText: '', submitting: false })}
        >
          <div
            className="bg-bone rounded-t-3xl sm:rounded-3xl p-7 sm:p-8 w-full sm:max-w-lg relative animate-fade-up"
            onClick={(e) => e.stopPropagation()}
            style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}
          >
            <p className="text-red-700 font-sans text-[10px] tracking-[0.35em] uppercase mb-2">Permanent in 30 days</p>
            <h3 className="font-serif text-2xl sm:text-3xl font-light text-dark leading-tight mb-3 mystical-heading">
              Are you sure?
            </h3>
            <p className="font-sans text-sm text-text-muted leading-relaxed mb-5">
              We'll disable your account now and start a 30-day countdown. Within those 30 days, email{' '}
              <a href="mailto:client_care@liveinvibrancy.com" className="text-gold hover:underline">client_care@liveinvibrancy.com</a> to restore.
              After 30 days, your data is permanently deleted.
            </p>

            <label className="block mb-4">
              <span className="font-sans text-xs text-text-muted tracking-wider uppercase">Password</span>
              <Input
                type="password"
                value={deleteState.password}
                onChange={(e) => setDeleteState(s => ({ ...s, password: e.target.value }))}
                data-testid="self-delete-password"
                placeholder="Confirm with your password"
                className="h-11 mt-2 bg-sand/50 border-black/8 focus:border-red-400 focus:ring-1 focus:ring-red-300 rounded-xl font-sans"
              />
              <span className="font-sans text-[10px] text-text-muted/70 mt-1 block">
                If you signed in with Google only, leave this blank.
              </span>
            </label>

            <label className="block mb-6">
              <span className="font-sans text-xs text-text-muted tracking-wider uppercase">
                Type <code className="px-1 py-0.5 rounded bg-bone border border-black/10 font-mono">DELETE MY ACCOUNT</code> to confirm
              </span>
              <input
                type="text"
                value={deleteState.confirmText}
                onChange={(e) => setDeleteState(s => ({ ...s, confirmText: e.target.value }))}
                data-testid="self-delete-confirm-input"
                placeholder="DELETE MY ACCOUNT"
                className="mt-2 w-full h-12 px-3 bg-sand/50 border border-black/8 focus:border-red-400 focus:ring-1 focus:ring-red-300 rounded-xl font-mono text-sm outline-none"
              />
            </label>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => setDeleteState({ open: false, password: '', confirmText: '', submitting: false })}
                disabled={deleteState.submitting}
                data-testid="self-delete-cancel"
                className="font-sans text-xs text-text-muted h-11"
              >
                Cancel
              </Button>
              <Button
                onClick={submitSelfDelete}
                disabled={deleteState.submitting || deleteState.confirmText !== 'DELETE MY ACCOUNT'}
                data-testid="self-delete-confirm-button"
                className="bg-red-600 hover:bg-red-700 text-white rounded-xl font-sans text-xs tracking-[0.25em] uppercase h-11 px-6 disabled:opacity-40"
              >
                <Trash2 size={14} className="mr-2" />
                {deleteState.submitting ? 'Deleting...' : 'Delete my account'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
